import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidationForm, ValidatingTextArea, ValidatingInput, ValidationSummary } from '@/components/forms/validation';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import theme from './ChangeRequestCreateDialog.module.scss';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import ChangeRequestAttachmentList from '@/seller/components/change-requests/ChangeRequestAttachmentList';
import { ChangeRequestAttachment, ChangeRequest } from '@/types';
import { Modal, ModalBody, ModalHeader } from '@ygtechnologies/bygr-frontend-component-library';
import { createChangeRequest, CreateChangeRequestOptions } from '@/services/change-requests';

interface ChangeRequestCreateDialogProps {
    residentialUnitId: number;
    show: boolean;
    onClose(): void;
    onSubmit?: (changeRequest: ChangeRequest) => void;
}

const ChangeRequestCreateDialog: React.FC<ChangeRequestCreateDialogProps> = ({
    residentialUnitId,
    show,
    onSubmit,
    onClose,
}) => {
    const [saving, setSaving] = useState(false);
    const [subject, setSubject] = useState('');
    const [description, setDescription] = useState('');
    const [internalDescription, setInternalDescription] = useState('');

    const [attachments, setAttachments] = useState<ChangeRequestAttachment[]>([]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const request: CreateChangeRequestOptions = {
            residentialUnitId,
            subject,
            description,
            attachments,
            internalDescription,
        };

        setSaving(true);

        try {
            const changeRequest = await createChangeRequest(request);
            triggerHandler(onClose);
            triggerHandler(onSubmit, changeRequest);
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setSubject('');
        setDescription('');
        setInternalDescription('');
        setAttachments([]);
    };

    return (
        <Modal size="lg" isOpen={show} toggle={onClose} onOpen={reset}>
            <ModalHeader>
                <h6>
                    <Trans>Create new change request</Trans>
                </h6>
            </ModalHeader>
            <ModalBody>
                <ValidationForm onSubmit={handleSubmit} className={theme.form}>
                    <div className="form-group">
                        <label>
                            <Trans>Title</Trans>
                        </label>
                        <ValidatingInput
                            fieldName={t`Title`}
                            required
                            className="form-control"
                            placeholder={t`What is the change about? Also, a text that will help you find again this change request easily.`}
                            value={subject}
                            name="subject"
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label>
                            <Trans>Description</Trans>
                        </label>
                        <ValidatingTextArea
                            fieldName={t`Description`}
                            required
                            className="form-control"
                            rows={10}
                            placeholder={t`Description visible to the buyer.`}
                            value={description}
                            name="description"
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>
                            <Trans>Internal description</Trans>
                        </label>
                        <ValidatingTextArea
                            fieldName={t`Internal description`}
                            required
                            className="form-control"
                            rows={10}
                            placeholder={t`Internal description. Not visible to buyers.`}
                            value={internalDescription}
                            name="description"
                            onChange={(e) => setInternalDescription(e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <ChangeRequestAttachmentList
                            useUploadWizard
                            residentialUnitId={residentialUnitId}
                            value={attachments}
                            onChange={(newValue) => setAttachments(newValue)}
                            allowPaste
                        />
                    </div>

                    <ValidationSummary />
                    <ButtonBar margins="top">
                        <ButtonBar.Right>
                            <Button onClick={onClose} variant="tertiary">
                                <Trans>Discard</Trans>
                            </Button>
                            <Button type="submit" variant="primary" loading={saving}>
                                <Trans>Create</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ValidationForm>
            </ModalBody>
        </Modal>
    );
};

export default ChangeRequestCreateDialog;
